import { api } from './client';

// Types
export interface DashboardStats {
  users: UserStats;
  projects: ProjectStats;
  revenue: RevenueStats;
  usage: UsageStats;
}

export interface UserStats {
  total: number;
  newToday: number;
  newThisWeek: number;
  newThisMonth: number;
  byTier: Record<string, number>;
}

export interface ProjectStats {
  total: number;
  active: number;
  paused: number;
  stopped: number;
}

export interface RevenueStats {
  mrr: number;
  thisMonth: number;
  lastMonth: number;
}

export interface UsageStats {
  aiTokensToday: number;
  containerHoursToday: number;
  activeContainers: number;
}

export interface ActivityItem {
  type: string;
  message: string;
  timestamp: string;
}

export interface AdminUser {
  id: string;
  keycloakId: string;
  email: string;
  displayName: string;
  subscriptionTier: string;
  role: string;
  creditsBalance: number;
  projectCount: number;
  lastLoginAt?: string;
  createdAt: string;
}

export interface AdminUserDetail extends AdminUser {
  avatarUrl?: string;
  isActive: boolean;
  stripeCustomerId?: string;
  githubUsername?: string;
  projects: AdminProjectSummary[];
}

export interface AdminProjectSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AdminProject {
  id: string;
  name: string;
  slug: string;
  status: string;
  stackType: string;
  hasDatabase: boolean;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  createdAt: string;
  lastDeployedAt?: string;
  lastActivityAt: string;
}

export interface AdminProjectDetail extends AdminProject {
  description?: string;
  databaseName?: string;
  databaseSizeMb: number;
  containerStatus?: string;
  customDomains: string[];
  gitHubRepo?: string;
}

export interface CertificateOverview {
  summary: CertificateSummary;
  certificates: Certificate[];
}

export interface CertificateSummary {
  total: number;
  active: number;
  expiringSoon: number;
  failed: number;
  expired: number;
}

export interface Certificate {
  id: string;
  domain: string;
  projectId: string;
  projectName: string;
  userEmail: string;
  sslType: string;
  sslStatus: string;
  expiresAt?: string;
  lastRenewalAttempt: string;
  status: string;
}

export interface CertificateLog {
  id: string;
  success: boolean;
  error?: string;
  attemptedAt: string;
}

export interface AuditLog {
  id: string;
  userId?: string;
  userEmail?: string;
  action: string;
  entityType: string;
  entityId?: string;
  oldValues?: string;
  newValues?: string;
  ipAddress?: string;
  createdAt: string;
}

// API Functions
export const adminApi = {
  // Stats
  getDashboardStats: () =>
    api.get<DashboardStats>('/api/admin/stats/dashboard'),

  getActivity: (hours = 24) =>
    api.get<ActivityItem[]>(`/api/admin/stats/activity?hours=${hours}`),

  // Users
  getUsers: (params: { page?: number; pageSize?: number; search?: string; tier?: string }) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('pageSize', params.pageSize.toString());
    if (params.search) query.set('search', params.search);
    if (params.tier) query.set('tier', params.tier);
    return api.get<PaginatedResponse<AdminUser>>(`/api/admin/users?${query}`);
  },

  getUser: (id: string) =>
    api.get<AdminUserDetail>(`/api/admin/users/${id}`),

  updateUser: (id: string, data: { displayName?: string; role?: string; subscriptionTier?: string }) =>
    api.put<void>(`/api/admin/users/${id}`, data),

  addCredits: (id: string, amount: number, reason: string) =>
    api.post<{ newBalance: number }>(`/api/admin/users/${id}/add-credits`, { amount, reason }),

  deleteUser: (id: string) =>
    api.delete(`/api/admin/users/${id}`),

  // Projects
  getProjects: (params: { page?: number; pageSize?: number; search?: string; status?: string; userId?: string }) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('pageSize', params.pageSize.toString());
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.userId) query.set('userId', params.userId);
    return api.get<PaginatedResponse<AdminProject>>(`/api/admin/projects?${query}`);
  },

  getProject: (id: string) =>
    api.get<AdminProjectDetail>(`/api/admin/projects/${id}`),

  deleteProject: (id: string) =>
    api.delete(`/api/admin/projects/${id}`),

  rebuildProject: (id: string) =>
    api.post<{ message: string }>(`/api/admin/projects/${id}/rebuild`),

  // Certificates
  getCertificates: () =>
    api.get<CertificateOverview>('/api/admin/certificates'),

  renewCertificate: (id: string) =>
    api.post<{ message: string }>(`/api/admin/certificates/${id}/renew`),

  getCertificateLogs: (id: string) =>
    api.get<CertificateLog[]>(`/api/admin/certificates/${id}/logs`),

  // Audit Logs
  getAuditLogs: (params: { page?: number; pageSize?: number; userId?: string; action?: string; entityType?: string; from?: string; to?: string }) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('pageSize', params.pageSize.toString());
    if (params.userId) query.set('userId', params.userId);
    if (params.action) query.set('action', params.action);
    if (params.entityType) query.set('entityType', params.entityType);
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    return api.get<PaginatedResponse<AuditLog>>(`/api/admin/logs/audit?${query}`);
  },

  getAuditActions: () =>
    api.get<string[]>('/api/admin/logs/audit/actions'),

  getAuditEntityTypes: () =>
    api.get<string[]>('/api/admin/logs/audit/entity-types'),
};
